<template>
    <div class="availability-wrapper" @click="closeReservationModal">
        <common-content-spacer height="huge">
            <form-row v-if="prefill.id">
                <form-cell width="half">
                    <text-input
                        :label="$t('components.availability.add_availability.availability_title.label')"
                        :placeholder="$t('components.availability.add_availability.availability_title.placeholder')"
                        :prefillVal="timelineTitle"
                        :showInfoTip="true"
                        :infoTipMessage="$t('components.availability.add_availability.availability_title.tool_tip_message')"
                        :containerDimensions="containerDimensions"
                        @change="setTimelineTitle"
                    ></text-input>
                </form-cell>

                <form-cell width="half">
                    <text-input
                        class="availability-inputs__item"
                        type="number"
                        :label="$t('components.availability.add_availability.booking_days_limit.label')"
                        :placeholder="$t('components.availability.add_availability.booking_days_limit.placeholder')"
                        :prefillVal="bookingDaysLimit"
                        :showInfoTip="true"
                        :infoTipMessage="$t('components.availability.add_availability.booking_days_limit.tool_tip_message')"
                        :containerDimensions="containerDimensions"
                        @change="bookingDaysChanged"
                    ></text-input>
                </form-cell>
            </form-row>
            <form-row v-if="prefill.id">
                <form-cell width="half">
                    <datepicker-input
                        ref="validFrom"
                        class="availability-inputs__item"
                        :label="$t('forms.reservations.reservation_filter.fields.date_from.label')"
                        :placeholder="$t('forms.reservations.reservation_filter.fields.date_from.placeholder')"
                        :upperLimit="upperLimit"
                        :showInfoTip="true"
                        :infoTipMessage="$t('forms.reservations.reservation_filter.fields.date_from.tool_tip_message')"
                        :containerDimensions="containerDimensions"
                        @date-changed="dateFromChanged"
                    ></datepicker-input>
                </form-cell>
                <form-cell width="half">
                    <datepicker-input
                        ref="validTo"
                        class="availability-inputs__item"
                        :label="$t('forms.reservations.reservation_filter.fields.date_to.label')"
                        :placeholder="$t('forms.reservations.reservation_filter.fields.date_to.placeholder')"
                        :lowerLimit="lowerLimit"
                        :showInfoTip="true"
                        :infoTipMessage="$t('forms.reservations.reservation_filter.fields.date_to.tool_tip_message')"
                        :containerDimensions="containerDimensions"
                        @date-changed="dateToChanged"
                    ></datepicker-input>
                </form-cell>
            </form-row>

            <availability-summary
                :user="selectedUser"
                :slots="selectedSlots"
                :selectedDay="selectedDay"
                @select-day="selectDay"
                @click="scrollTo"
            ></availability-summary>

            <availability-time-picker
                ref="availabilityTimePicker"
                :appointmentLength="appointmentLength"
                :originalSlots="selectedSlotsForDay"
                @slots-selected="slotsSelected"
            ></availability-time-picker>

            <user-availability
                ref="userAvailability"
                :offer="offer"
                :prefill="prefill"
                :existingAvailability="existingAvailability"
                @toggle-performer="onTogglePerformer"
            ></user-availability>

            <common-content-wrapper width="tiny">
                <div class="availability-button">
                    <common-button
                        type="tertiary"
                        size="large-long"
                        :isProcessing="isLoading"
                        :isDisabled="isSaveDisabled"
                        :label="$t('common.microcopy.save')"
                        :query="query"
                        @click="saveAvailabilityClick()"
                    ></common-button>
                </div>
            </common-content-wrapper>
        </common-content-spacer>

        <out-of-availability-modal
            v-if="isOutOfAvailabilityModalOpen"
            :offers="offersOutOfAvailability"
            @close="closeOutOfAvailabilityModal"
            @accepted="updateAvailability"
        ></out-of-availability-modal>

        <common-yes-no-modal
            v-if="isChangesNotSavedModalOpen"
            :showWarningIcon="true"
            :headingText="$t('common.general.not_saved')"
            :noButtonText="$t('common.microcopy.false')"
            :yesButtonText="$t('common.microcopy.true')"
            height="tiny"
            @close="closeYesNoModal"
            @continue="saveChangeDate"
        ></common-yes-no-modal>
        <add-availability-modal
            v-if="isAddAvailabilityModalOpen"
            :query="query"
            @success="emitEvent({ name: 'availability-added' })"
            @close="closeAddAvailabilityModal"
        ></add-availability-modal>
        <add-slots-to-multiple-day-modal
            v-if="isAddSlotsForMultipleDaysOpen"
            :days="daysForModal"
            :selectedDay="selectedDay"
            width="wide"
            @success="applySLotsToMultipleDays($event)"
            @close="closeAddSlotToMultipleDayModal"
        ></add-slots-to-multiple-day-modal>
    </div>
</template>

<script>
    import dayjs from 'dayjs'
    import isEqual from 'lodash.isequal'
    import OfferAPI from 'api/OfferAPI'
    import AvailabilitySummary from 'components/page-elements/availability/AvailabilitySummary'
    import AvailabilityTimePicker from 'components/page-elements/availability/AvailabilityTimePicker'
    import OutOfAvailabilityModal from 'components/modals/availability/OutOfAvailabilityModal'
    import dayjsPluginUTC from 'dayjs/plugin/utc'
    import availabilityTransformer from 'transformers/availabilityTransformer'
    import DaysEnum from 'enums/days.enum'
    import cloneDeep from 'lodash.clonedeep'
    import UserAvailability from 'components/page-elements/availability/UserAvailability'
    import AvailabilityUserRoleEnum from 'enums/availability_user_role.enum'
    import FormMixin from 'mixins/FormMixin'
    import WindowResizeMixin from 'mixins/WindowResizeMixin'
    import AddAvailabilityModal from 'components/modals/company/AddAvailabilityModal'
    import addSlotsToMultipleDayModal from 'components/modals/availability/addSlotsToMultipleDayModal'
    import { dateFormat } from '~/helpers'
    import Store from '~/store'

    dayjs.extend(dayjsPluginUTC)

    const EMPTY_SCHEDULE = { mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] }

    export default {
        mixins: [FormMixin, WindowResizeMixin],
        components: {
            AvailabilitySummary,
            AvailabilityTimePicker,
            UserAvailability,
            OutOfAvailabilityModal,
            AddAvailabilityModal,
            addSlotsToMultipleDayModal,
        },

        props: {
            offer: { type: Object, required: true },
            existingAvailability: { type: Object, required: false, default: undefined },
            prefill: {
                type: Object,
                default: cloneDeep({ timeline: EMPTY_SCHEDULE, title: '' }),
            },
        },

        data() {
            return {
                selectedDay: DaysEnum.en.MONDAY.value,
                selectDayBackup: null,
                startSlots: cloneDeep(EMPTY_SCHEDULE),
                selectedSlots: cloneDeep(EMPTY_SCHEDULE),
                selectedUser: {},
                isLoading: false,
                isOutOfAvailabilityModalOpen: false,
                offersOutOfAvailability: [],
                timelineTitle: '',
                upperLimit: null,
                lowerLimit: null,
                validFrom: null,
                validTo: null,
                bookingDaysLimit: null,
                isChangesNotSavedModalOpen: false,
                isAddAvailabilityModalOpen: false,
                isAddSlotsForMultipleDaysOpen: false,
                lastChosenSLots: [],
                query: {},
                containerDimensions: undefined,
                daysForModal: cloneDeep(EMPTY_SCHEDULE),
            }
        },

        computed: {
            selectedSlotsForDay() {
                return this.selectedSlots[this.selectedDay]
            },

            isSaveDisabled() {
                return isEqual(EMPTY_SCHEDULE, this.selectedSlots)
            },

            appointmentLength() {
                return 30
            },
        },

        methods: {
            getContainerDimensions() {
                const el = document.getElementsByClassName('availability-wrapper')[0]
                if (el) {
                    this.containerDimensions = { width: el.clientWidth }
                }
            },

            openAddSlotForMultipleDays() {
                this.isAddSlotsForMultipleDaysOpen = true
            },

            closeAddSlotToMultipleDayModal() {
                this.isAddSlotsForMultipleDaysOpen = false
            },

            slotsSelected(slots) {
                this.selectedSlots[this.selectedDay] = slots
                this.lastChosenSLots = slots
                this.openAddSlotForMultipleDays()
            },

            applySLotsToMultipleDays(payload) {
                payload.forEach((day) => {
                    this.selectedSlots[day] = this.lastChosenSLots
                })
                this.closeAddSlotToMultipleDayModal()
            },

            closeYesNoModal() {
                this.isChangesNotSavedModalOpen = false
                this.setDateAndRemoveBackup()
            },

            saveChangeDate() {
                this.isChangesNotSavedModalOpen = false
                this.$refs.availabilityTimePicker.acceptSlots()
                this.setDateAndRemoveBackup()
            },

            setDateAndRemoveBackup() {
                this.selectedDay = this.selectDayBackup
                this.selectDayBackup = null
            },

            onTogglePerformer(payload) {
                this.selectedUser = payload.user
            },

            selectDay(day) {
                if (!this.$refs.availabilityTimePicker.areActionsDisabled) {
                    this.isChangesNotSavedModalOpen = true
                    this.selectDayBackup = day
                    return
                }
                this.selectedDay = day
            },

            saveAvailabilityClick() {
                if (!this.prefill?.id) {
                    this.isAddAvailabilityModalOpen = !this.isAddAvailabilityModalOpen
                    return this.saveAvailability()
                }
                this.checkForAffectedAvailabilityEvents()
            },

            checkForAffectedAvailabilityEvents() {
                Store.dispatch('configuration/setIsLoading', true)
                OfferAPI.getAffectedAvailabilityEvents({
                    availabilityIds: [this.prefill?.id],
                    timeline: availabilityTransformer.timelineMinutesLocalToMinutesUTC(
                        this.selectedSlots,
                        this.offer.location.timeZone,
                    ),
                    validFrom: this.validFrom,
                    validTo: this.validTo,
                }).then((res) => {
                    if (!res.outOfTimeOffers.length) {
                        this.updateAvailability()
                    } else {
                        this.offersOutOfAvailability = res.outOfTimeOffers
                        this.showOutOfAvailabilityModal()
                    }
                    Store.dispatch('configuration/setIsLoading', false)
                })
            },

            updateAvailability() {
                this.isLoading = true
                const request = {
                    availabilityId: this.prefill?.id,
                    timeline: availabilityTransformer.timelineMinutesLocalToMinutesUTC(
                        this.selectedSlots,
                        this.offer.location.timeZone,
                    ),
                    timelineTitle: this.timelineTitle,

                    performerId: this.$refs.userAvailability?.selectedUsers.find((us) => us.user?.isPerformer).user.id,
                    moderatorIds: this.$refs.userAvailability?.selectedUsers
                        .filter((us) => us.user.isModerator)
                        .map((item) => item.user.id),
                    moderatorInvites: this.$refs.userAvailability?.inviteUsers.map((us) =>
                        us.email ? us.email : us.invitedUser?.email,
                    ),
                    validFrom: this.validFrom,
                    validTo: this.validTo,
                    bookingDaysLimit: Number(this.bookingDaysLimit) || null,
                }

                if (this.offer?.location?.companyServiceProvider?.id) {
                    request.companyId = this.offer.location.companyServiceProvider.id
                    OfferAPI.updateCompanyAvailability(request).then(() => {
                        this.isLoading = false
                        this.emitEvent({ name: 'availability-added' })
                    })
                } else {
                    OfferAPI.updateUserAvailability(request).then(() => {
                        this.isLoading = false
                        this.emitEvent({ name: 'availability-added' })
                    })
                }
            },

            scrollTo() {
                if (this.isSmartphoneLarge) {
                    const el = this.$refs.availabilityTimePicker.$el
                    el.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                        inline: 'nearest',
                    })
                }
            },

            saveAvailability() {
                this.query = {
                    timeline: availabilityTransformer.timelineMinutesLocalToMinutesUTC(
                        this.selectedSlots,
                        this.offer.location.timeZone,
                    ),
                    companyId: this.offer.location?.companyServiceProvider?.id,
                    timelineTitle: this.timelineTitle,
                    performerId: this.$refs.userAvailability?.selectedUsers.find((us) => us.user?.isPerformer).user?.id,
                    moderatorIds: this.$refs.userAvailability?.selectedUsers
                        .filter((us) => us.user?.isModerator)
                        .map((item) => item?.user?.id),
                    moderatorInvites: this.$refs.userAvailability?.inviteUsers.map((us) =>
                        us.email ? us.email : us.invitedUser?.email,
                    ),
                    validFrom: this.validFrom,
                    validTo: this.validTo,
                    offer: this.offer,
                    selectedSlots: this.selectedSlots,
                }
            },

            showOutOfAvailabilityModal() {
                this.isOutOfAvailabilityModalOpen = true
            },

            closeOutOfAvailabilityModal() {
                this.isOutOfAvailabilityModalOpen = false
            },

            closeAddAvailabilityModal() {
                this.isAddAvailabilityModalOpen = false
            },

            setTimelineTitle({ value }) {
                if (value !== undefined) {
                    this.timelineTitle = value
                }
            },

            dateFromChanged(value) {
                if (value !== undefined) {
                    this.lowerLimit = dateFormat(value)
                    this.validFrom = value
                } else {
                    this.lowerLimit = new Date()
                    this.validFrom = null
                }
            },
            dateToChanged(value) {
                if (value !== undefined) {
                    this.upperLimit = dateFormat(value)
                    this.validTo = value
                } else {
                    this.upperLimit = null
                    this.validTo = null
                }
            },

            bookingDaysChanged({ value }) {
                if (value !== undefined) {
                    this.bookingDaysLimit = value
                }
            },
        },

        mounted() {
            this.getContainerDimensions()
            if (this.prefill?.validFrom) {
                this.$refs.validFrom.selected = dateFormat(this.prefill.validFrom)
            }

            if (this.prefill?.validTo) {
                this.$refs.validTo.selected = dateFormat(this.prefill.validTo)
            }
            this.bookingDaysLimit = this.prefill.bookingDaysLimit
            this.startSlots = availabilityTransformer.timelineMinutesUTCToMinutesLocal(
                this.prefill.timeline,
                this.offer.location.timeZone,
            )
            this.selectedSlots = availabilityTransformer.timelineMinutesUTCToMinutesLocal(
                this.prefill.timeline,
                this.offer.location.timeZone,
            )
            this.timelineTitle = this.prefill.titleTextContent?.originalText
            this.selectedUser = this.prefill?.availabilityUsers?.find(
                (us) => us.function === AvailabilityUserRoleEnum.PERFORMER,
            ).user
        },
    }
</script>

<style lang="scss">
    .availability-inputs {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        align-items: center;
        max-width: 800px;
        margin: 0 auto;

        &__item {
            @media only screen and (max-width: 600px) {
                width: 100%;
            }
        }
    }
    .availability-button {
        display: flex;
        justify-content: center;
    }
</style>
